/**
 * Asset Upload / Scan Asset
 *
 * Unified inline asset identification modal for the Command Center.
 * Allows users to take a photo (camera) or upload an image (file picker)
 * to identify an asset without leaving the conversational interface.
 *
 * Flow:
 * 1. User opens modal → chooses "Take Photo" or "Upload Image"
 * 2. Image is uploaded to Firebase Storage
 * 3. GPS is requested (optional — gracefully degrades for office users)
 * 4. Image + GPS sent to asset recognition API (OCR + visual matching)
 * 5. Results shown inline — user confirms match or adds new asset
 * 6. Confirmed result injected into chat context for AI follow-up
 */

import { useState, useRef, useCallback } from "react";
import { Modal, Button, Progress, Tag, message } from "antd";
import {
	CameraOutlined,
	CloudUploadOutlined,
	CloseOutlined,
	LoadingOutlined,
	CheckCircleOutlined,
	WarningOutlined,
	InfoCircleOutlined,
	PlusOutlined,
	ReloadOutlined,
	ScanOutlined,
} from "@ant-design/icons";
import { uploadPhotoToFirebase } from "@/services/storage-service";
import {
	recognizeAsset,
	getHighAccuracyLocation,
} from "@/services/asset-recognition-service";
import type {
	AssetRecognitionResponse,
	AssetMatch,
	GPSLocation,
} from "@/models/assets/asset-recognition.model";
import "./AssetUpload.scss";

/** Result passed back to CommandCenter when user confirms a match */
export interface AssetUploadResult {
	/** Summary text to inject as user message in chat */
	chatMessage: string;
	/** The full recognition response for building cards */
	recognition: AssetRecognitionResponse;
	/** URL of the uploaded photo */
	photoUrl: string;
}

type ScanStep = "choose" | "camera" | "processing" | "results";

interface AssetUploadProps {
	open: boolean;
	onClose: () => void;
	onResult: (result: AssetUploadResult) => void;
	onAddNewAsset: () => void;
}

const AssetUpload = ({ open, onClose, onResult, onAddNewAsset }: AssetUploadProps) => {
	const [step, setStep] = useState<ScanStep>("choose");
	const [isProcessing, setIsProcessing] = useState(false);
	const [processingStatus, setProcessingStatus] = useState("");
	const [processingProgress, setProcessingProgress] = useState(0);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
	const [recognitionResult, setRecognitionResult] = useState<AssetRecognitionResponse | null>(null);
	const [photoUrl, setPhotoUrl] = useState<string>("");

	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	// ─── Reset state ───
	const resetState = useCallback(() => {
		setStep("choose");
		setIsProcessing(false);
		setProcessingStatus("");
		setProcessingProgress(0);
		setPreviewUrl(null);
		setRecognitionResult(null);
		setPhotoUrl("");
		if (cameraStream) {
			cameraStream.getTracks().forEach((t) => t.stop());
			setCameraStream(null);
		}
	}, [cameraStream]);

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleScanAgain = () => {
		setRecognitionResult(null);
		setPhotoUrl("");
		setPreviewUrl(null);
		setStep("choose");
	};

	// ─── GPS (best effort) ───
	const getGPS = async (): Promise<GPSLocation | null> => {
		try {
			return await getHighAccuracyLocation();
		} catch {
			// GPS unavailable — office user; proceed without it
			return null;
		}
	};

	// ─── Process image through recognition ───
	const processImage = async (blob: Blob) => {
		setStep("processing");
		setIsProcessing(true);

		try {
			// Step 1: Upload to Firebase
			setProcessingStatus("Uploading photo...");
			setProcessingProgress(20);
			const filename = `scan-${Date.now()}.jpg`;
			const uploadedUrl = await uploadPhotoToFirebase(blob, "asset-recognition", filename) as string;
			setPhotoUrl(uploadedUrl);

			// Step 2: Get GPS
			setProcessingStatus("Getting location...");
			setProcessingProgress(40);
			const gps = await getGPS();

			// Step 3: Call recognition API
			setProcessingStatus("Identifying asset...");
			setProcessingProgress(60);
			const recognition = await recognizeAsset({
				photoUrl: uploadedUrl,
				latitude: gps?.latitude ?? 0,
				longitude: gps?.longitude ?? 0,
				accuracy: gps?.accuracy ?? 9999, // Large accuracy = wide search for office users
			});

			setProcessingProgress(100);
			setProcessingStatus("Done!");
			setRecognitionResult(recognition);
			setStep("results");
		} catch (error: any) {
			console.error("Asset recognition error:", error);
			message.error(error.message || "Failed to identify asset. Please try again.");
			setStep("choose");
		} finally {
			setIsProcessing(false);
		}
	};

	// ─── Build chat message from recognition ───
	const formatRecognitionResult = (recognition: AssetRecognitionResponse): string => {
		if (recognition.matches.length === 0) {
			const extracted = recognition.extractedData;
			const parts: string[] = ["I scanned an asset photo but no match was found in the system."];
			if (extracted.manufacturer?.text) parts.push(`Manufacturer appears to be: ${extracted.manufacturer.text}`);
			if (extracted.assetType?.text) parts.push(`Type: ${extracted.assetType.text}`);
			if (extracted.modelNumber?.text) parts.push(`Model: ${extracted.modelNumber.text}`);
			if (extracted.serialNumber?.text) parts.push(`Serial: ${extracted.serialNumber.text}`);
			parts.push("Can you help me identify this equipment?");
			return parts.join(". ");
		}

		const topMatch = recognition.matches[0];
		const asset = topMatch.asset;
		const confidence = Math.round(topMatch.confidence * 100);
		const tag = asset.asset_tag || asset.assetTag || asset.assetType?.name || "Unknown";
		const type = asset.assetType?.name || asset.asset_type_name || "Equipment";
		const location = asset.location?.name || asset.location_name || "";

		let msg = `[Scanned asset photo] Identified: ${tag} (${type})`;
		if (location) msg += ` at ${location}`;
		msg += `, ${confidence}% confidence.`;
		msg += " Tell me about this asset — its condition, maintenance history, and anything I should know.";

		return msg;
	};

	// ─── Confirm a match and inject into chat ───
	const handleConfirmMatch = () => {
		if (!recognitionResult) return;
		const chatMessage = formatRecognitionResult(recognitionResult);
		onResult({ chatMessage, recognition: recognitionResult, photoUrl });
		handleClose();
	};

	// ─── Add as new asset ───
	const handleAddNew = () => {
		handleClose();
		onAddNewAsset();
	};

	// ─── Confidence helpers ───
	const getConfidenceLevel = (confidence: number): "high" | "medium" | "low" => {
		if (confidence >= 0.8) return "high";
		if (confidence >= 0.6) return "medium";
		return "low";
	};

	const getConfidenceColor = (confidence: number): string => {
		const level = getConfidenceLevel(confidence);
		if (level === "high") return "success";
		if (level === "medium") return "warning";
		return "default";
	};

	const getConfidenceIcon = (confidence: number) => {
		const level = getConfidenceLevel(confidence);
		if (level === "high") return <CheckCircleOutlined />;
		if (level === "medium") return <WarningOutlined />;
		return <InfoCircleOutlined />;
	};

	// ─── Camera Flow ───
	const startCamera = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: "environment", width: { ideal: 1920 }, height: { ideal: 1080 } },
			});
			setCameraStream(stream);
			setStep("camera");

			setTimeout(() => {
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					videoRef.current.play();
				}
			}, 100);
		} catch (error: any) {
			console.error("Camera error:", error);
			message.error("Unable to access camera. Try uploading an image instead.");
		}
	};

	const capturePhoto = () => {
		if (!videoRef.current || !canvasRef.current) return;
		const video = videoRef.current;
		const canvas = canvasRef.current;
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		ctx.drawImage(video, 0, 0);

		if (cameraStream) {
			cameraStream.getTracks().forEach((t) => t.stop());
			setCameraStream(null);
		}

		canvas.toBlob(
			(blob) => {
				if (blob) {
					setPreviewUrl(canvas.toDataURL("image/jpeg", 0.85));
					processImage(blob);
				}
			},
			"image/jpeg",
			0.85
		);
	};

	// ─── File Upload Flow ───
	const handleFileSelect = (file: File) => {
		const reader = new FileReader();
		reader.onload = (e) => setPreviewUrl(e.target?.result as string);
		reader.readAsDataURL(file);
		processImage(file);
	};

	const triggerFileInput = () => {
		fileInputRef.current?.click();
	};

	const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) handleFileSelect(file);
		// Reset so the same file can be re-selected
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	// ─── Render match card ───
	const renderMatchCard = (match: AssetMatch, index: number) => {
		const asset = match.asset;
		const confidence = Math.round(match.confidence * 100);
		const tag = asset.asset_tag || asset.assetTag || "—";
		const type = asset.assetType?.name || asset.asset_type_name || "Unknown Type";
		const location = asset.location?.name || asset.location_name || "—";
		const manufacturer = asset.manufacturer?.name || asset.manufacturer_name || "—";
		const serial = asset.serialNumber || asset.serial_number || "—";
		const isTop = index === 0;

		return (
			<div key={index} className={`au-match-card ${isTop ? "au-match-top" : ""}`}>
				<div className="au-match-header">
					<span className="au-match-type">{type}</span>
					<Tag color={getConfidenceColor(match.confidence)} icon={getConfidenceIcon(match.confidence)}>
						{confidence}% Match
					</Tag>
				</div>
				<div className="au-match-details">
					<div className="au-match-field">
						<span className="au-match-label">Asset Tag</span>
						<span className="au-match-value">{tag}</span>
					</div>
					<div className="au-match-field">
						<span className="au-match-label">Location</span>
						<span className="au-match-value">{location}</span>
					</div>
					<div className="au-match-field">
						<span className="au-match-label">Manufacturer</span>
						<span className="au-match-value">{manufacturer}</span>
					</div>
					<div className="au-match-field">
						<span className="au-match-label">Serial #</span>
						<span className="au-match-value">{serial}</span>
					</div>
				</div>
				{/* Matched fields */}
				{match.matchedFields && (
					<div className="au-matched-via">
						{match.matchedFields.serialNumber && <Tag>Serial Number</Tag>}
						{match.matchedFields.assetTag && <Tag>Asset Tag</Tag>}
						{match.matchedFields.manufacturer && <Tag>Manufacturer</Tag>}
						{match.matchedFields.modelNumber && <Tag>Model Number</Tag>}
						{match.matchedFields.assetType && <Tag>Asset Type</Tag>}
					</div>
				)}
				{isTop && (
					<Button
						type="primary"
						block
						className="au-confirm-btn"
						onClick={handleConfirmMatch}
					>
						Use This Asset
					</Button>
				)}
			</div>
		);
	};

	// ─── Render extracted OCR data ───
	const renderExtractedData = () => {
		if (!recognitionResult) return null;
		const { extractedData } = recognitionResult;
		const fields = [
			{ label: "Serial Number", data: extractedData.serialNumber },
			{ label: "Asset Tag", data: extractedData.assetTag },
			{ label: "Model Number", data: extractedData.modelNumber },
			{ label: "Manufacturer", data: extractedData.manufacturer },
			{ label: "Asset Type", data: extractedData.assetType },
			{ label: "Condition", data: extractedData.condition },
		].filter((f) => f.data);

		if (fields.length === 0) return null;

		return (
			<div className="au-extracted">
				<h4 className="au-extracted-title">Extracted Information</h4>
				{fields.map((field, i) => (
					<div key={i} className="au-extracted-row">
						<span className="au-extracted-label">{field.label}</span>
						<span className="au-extracted-value">
							{field.data!.text}
							<span className="au-extracted-confidence">
								{Math.round(field.data!.confidence * 100)}%
							</span>
						</span>
					</div>
				))}
			</div>
		);
	};

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			footer={null}
			closable={false}
			centered
			width={480}
			className="asset-upload-modal"
			destroyOnClose
		>
			<div className="au-container">
				{/* Header */}
				<div className="au-header">
					<div className="au-title-row">
						<div className="au-title-icon">
							<ScanOutlined />
						</div>
						<div>
							<h3 className="au-title">Scan Asset</h3>
							<p className="au-subtitle">
								{step === "choose" && "Capture or upload a photo of any tag, nameplate, QR code, or equipment."}
								{step === "camera" && "Point your camera at the asset and tap capture."}
								{step === "processing" && "Analyzing your photo..."}
								{step === "results" && recognitionResult && recognitionResult.matches.length > 0 && `Found ${recognitionResult.matches.length} match${recognitionResult.matches.length > 1 ? "es" : ""} across ${recognitionResult.assetsSearched} assets.`}
								{step === "results" && recognitionResult && recognitionResult.matches.length === 0 && "No matching assets found in your system."}
							</p>
						</div>
					</div>
					<button type="button" className="au-close" onClick={handleClose}>
						<CloseOutlined />
					</button>
				</div>

				{/* Step indicator */}
				<div className="au-steps">
					<div className={`au-step-dot ${step === "choose" ? "au-step-active" : (step !== "choose" ? "au-step-done" : "")}`} />
					<div className="au-step-line" />
					<div className={`au-step-dot ${step === "camera" || step === "processing" ? "au-step-active" : (step === "results" ? "au-step-done" : "")}`} />
					<div className="au-step-line" />
					<div className={`au-step-dot ${step === "results" ? "au-step-active" : ""}`} />
				</div>

				{/* Choose Mode */}
				{step === "choose" && (
					<div className="au-options">
						<button type="button" className="au-option" onClick={startCamera}>
							<div className="au-option-icon au-option-camera">
								<CameraOutlined />
							</div>
							<div className="au-option-text">
								<span className="au-option-label">Take Photo</span>
								<span className="au-option-desc">Use your camera to capture the asset</span>
							</div>
						</button>

						<button type="button" className="au-option" onClick={triggerFileInput}>
							<div className="au-option-icon au-option-upload">
								<CloudUploadOutlined />
							</div>
							<div className="au-option-text">
								<span className="au-option-label">Upload Image</span>
								<span className="au-option-desc">Choose a photo from your device</span>
							</div>
						</button>

						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							capture="environment"
							style={{ display: "none" }}
							onChange={onFileChange}
						/>
					</div>
				)}

				{/* Camera View */}
				{step === "camera" && (
					<div className="au-camera">
						<video ref={videoRef} className="au-camera-feed" autoPlay playsInline muted />
						<canvas ref={canvasRef} style={{ display: "none" }} />
						<div className="au-camera-controls">
							<Button
								type="default"
								onClick={() => {
									if (cameraStream) {
										cameraStream.getTracks().forEach((t) => t.stop());
										setCameraStream(null);
									}
									setStep("choose");
								}}
								className="au-camera-back"
							>
								Back
							</Button>
							<button type="button" className="au-capture-btn" onClick={capturePhoto}>
								<div className="au-capture-inner" />
							</button>
							<div style={{ width: 64 }} />
						</div>
					</div>
				)}

				{/* Processing */}
				{step === "processing" && (
					<div className="au-processing">
						{previewUrl && (
							<div className="au-preview">
								<img src={previewUrl} alt="Captured asset" className="au-preview-img" />
							</div>
						)}
						<div className="au-progress">
							<Progress
								percent={processingProgress}
								strokeColor="#92278f"
								trailColor="rgba(0,0,0,0.04)"
								showInfo={false}
								size="small"
							/>
							<div className="au-progress-status">
								<LoadingOutlined spin />
								<span>{processingStatus}</span>
							</div>
						</div>
					</div>
				)}

				{/* Results */}
				{step === "results" && recognitionResult && (
					<div className="au-results">
						{/* Photo preview (small) */}
						{previewUrl && (
							<div className="au-results-preview">
								<img src={previewUrl} alt="Scanned asset" className="au-results-preview-img" />
							</div>
						)}

						{/* Matches found */}
						{recognitionResult.matches.length > 0 && (
							<div className="au-matches">
								{recognitionResult.matches.slice(0, 3).map((match, i) => renderMatchCard(match, i))}
							</div>
						)}

						{/* No matches */}
						{recognitionResult.matches.length === 0 && (
							<div className="au-no-match">
								<div className="au-no-match-icon">
									<InfoCircleOutlined />
								</div>
								<p className="au-no-match-title">Asset Not Found</p>
								<p className="au-no-match-text">
									No matching assets were found in your system. You can add this as a new asset or try scanning again with a clearer photo.
								</p>
							</div>
						)}

						{/* Extracted OCR data */}
						{renderExtractedData()}

						{/* Action buttons */}
						<div className="au-results-actions">
							<Button
								icon={<ReloadOutlined />}
								onClick={handleScanAgain}
								block
								size="large"
								className="au-scan-again-btn"
							>
								Scan Again
							</Button>
							{recognitionResult.matches.length === 0 && (
								<Button
									type="primary"
									icon={<PlusOutlined />}
									onClick={handleAddNew}
									block
									size="large"
									className="au-add-new-btn"
								>
									Add as New Asset
								</Button>
							)}
						</div>
					</div>
				)}
			</div>
		</Modal>
	);
};

export { AssetUpload };
